// â”€â”€â”€ /my-test â€” Phase 6 smoke test surface â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
//
// Dev-only sanity check for userless access (Phase 6). End-to-end:
//   1. Enter email OR phone
//   2. Click "Send OTP"
//   3. Receive SMS / email magic link
//   4. Verify â†’ see "Signed in as {contact.displayName}"
//
// Gated by `contacts.useContactsForLogin` flag. NOT linked from main nav â€”
// access via direct URL only. Replaced by EPR's /my-reviews + /my-feedback
// (Phase 11) once integration lands.

import React, { useEffect, useState } from 'react';
import { ConfirmationResult, onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from '../config/firebase';
import {
  requestPhoneOtp,
  verifyPhoneOtp,
  requestEmailLink,
  isEmailLink,
  verifyEmailLink,
  userlessSignOut,
} from '../sdk/contacts';
import type { Contact } from '../types';

type Mode = 'phone' | 'email';

const MyTestPage: React.FC = () => {
  const [mode, setMode]               = useState<Mode>('phone');
  const [identifier, setIdentifier]   = useState('');
  const [otpCode, setOtpCode]         = useState('');
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null);
  const [contact, setContact]         = useState<Contact | null>(null);
  const [fbUser, setFbUser]           = useState<User | null>(null);
  const [busy, setBusy]               = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [info, setInfo]               = useState<string | null>(null);

  // Track Firebase Auth state so we can show "signed in as"
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setFbUser(u));
    return unsub;
  }, []);

  // Auto-complete email-link sign-in if user landed via the email link
  useEffect(() => {
    if (isEmailLink(window.location.href)) {
      void completeEmailLink();
    }
  }, []);

  async function completeEmailLink() {
    setBusy(true);
    setError(null);
    try {
      const c = await verifyEmailLink(window.location.href);
      setContact(c);
      setInfo(`Signed in as ${c.displayName} via email link.`);
      // Clean up URL
      window.history.replaceState(null, '', window.location.pathname);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleSend() {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (mode === 'phone') {
        const conf = await requestPhoneOtp(identifier, 'recaptcha-container');
        setConfirmation(conf);
        setInfo('SMS sent. Enter the 6-digit code below.');
      } else {
        await requestEmailLink(identifier, window.location.href);
        setInfo(
          `Sign-in link sent to ${identifier}. Open the email + click the link (same browser).`,
        );
      }
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleVerify() {
    if (!confirmation) return;
    setBusy(true);
    setError(null);
    try {
      const c = await verifyPhoneOtp(confirmation, otpCode);
      setContact(c);
      setInfo(`Signed in as ${c.displayName} via phone.`);
      setConfirmation(null);
      setOtpCode('');
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleSignOut() {
    await userlessSignOut();
    setContact(null);
    setInfo('Signed out.');
  }

  return (
    <div className="max-w-md mx-auto p-6 space-y-5">
      <div>
        <h1 className="text-xl font-bold text-stone-900">Userless Login Smoke Test</h1>
        <p className="text-sm text-stone-500 mt-1">
          Phase 6 sanity check. Sign in by email or phone â€” no <code>users/</code> doc
          required. Backed by Firebase Auth + <code>contacts/</code> lookup.
        </p>
      </div>

      {/* Current state */}
      <div className="bg-white border border-stone-200 rounded-lg p-4 text-sm space-y-1">
        <p className="text-stone-600">
          Firebase Auth: {fbUser
            ? <span className="text-emerald-700">signed in ({fbUser.email ?? fbUser.phoneNumber})</span>
            : <span className="text-stone-400">none</span>}
        </p>
        <p className="text-stone-600">
          Resolved contact: {contact
            ? <span className="text-emerald-700">{contact.displayName} ({contact.id})</span>
            : <span className="text-stone-400">none</span>}
        </p>
      </div>

      {fbUser ? (
        <button
          onClick={handleSignOut}
          className="w-full px-4 py-2 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg"
        >
          Sign out
        </button>
      ) : (
        <>
          {/* Mode tabs */}
          <div className="flex gap-1 border-b border-stone-200">
            <button
              onClick={() => { setMode('phone'); setError(null); setInfo(null); }}
              className={`px-4 py-2 text-sm border-b-2 ${
                mode === 'phone'
                  ? 'border-[#011132] text-[#011132] font-semibold'
                  : 'border-transparent text-stone-500'
              }`}
            >
              Phone OTP
            </button>
            <button
              onClick={() => { setMode('email'); setError(null); setInfo(null); }}
              className={`px-4 py-2 text-sm border-b-2 ${
                mode === 'email'
                  ? 'border-[#011132] text-[#011132] font-semibold'
                  : 'border-transparent text-stone-500'
              }`}
            >
              Email Link
            </button>
          </div>

          {/* Send identifier */}
          {!confirmation ? (
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-stone-700">
                  {mode === 'phone' ? 'WhatsApp / phone' : 'Email'}
                </span>
                <input
                  type={mode === 'phone' ? 'tel' : 'email'}
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder={mode === 'phone' ? '0812-3456-7890' : 'andi@rascalrepublic.com'}
                  className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm"
                />
              </label>
              <button
                onClick={handleSend}
                disabled={busy || !identifier.trim()}
                className="w-full px-4 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50"
                style={{ background: '#011132' }}
              >
                {busy ? 'Sendingâ€¦' : mode === 'phone' ? 'Send SMS OTP' : 'Send Email Link'}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-stone-700">
                  Enter 6-digit code from SMS
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  placeholder="123456"
                  className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm font-mono text-center text-lg tracking-widest"
                />
              </label>
              <button
                onClick={handleVerify}
                disabled={busy || otpCode.length !== 6}
                className="w-full px-4 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50"
                style={{ background: '#011132' }}
              >
                {busy ? 'Verifyingâ€¦' : 'Verify'}
              </button>
              <button
                onClick={() => { setConfirmation(null); setOtpCode(''); setError(null); }}
                className="w-full px-4 py-2 text-sm text-stone-500 hover:bg-stone-50 rounded"
              >
                Cancel â€” try again
              </button>
            </div>
          )}
        </>
      )}

      {/* Messages */}
      {info && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-800">
          {info}
        </div>
      )}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Invisible reCAPTCHA container for Phone Auth */}
      <div id="recaptcha-container" />

      {/* Footer */}
      <p className="text-[11px] text-stone-400 text-center">
        Dev surface. Replaced by EPR <code>/my-reviews</code> + <code>/my-feedback</code> in Phase 11.
      </p>
    </div>
  );
};

export default MyTestPage;
